import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { 
  Box, 
  Typography, 
  IconButton, 
  LinearProgress,
  Paper,
  Slider
} from '@mui/material';
import { 
  MusicNote as MusicIcon,
  Info as InfoIcon,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const PlayerContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2), rgba(99, 179, 237, 0.1))',
  backdropFilter: 'blur(10px)',
  color: 'white',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: '2px solid rgba(74, 144, 226, 0.3)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const TimeDisplay = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.7)',
  minWidth: '45px',
}));

const PlayButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(74, 144, 226, 0.15)',
  color: '#4a90e2',
  '&:hover': {
    backgroundColor: 'rgba(74, 144, 226, 0.25)',
  },
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  marginTop: theme.spacing(1),
}));

const PreviewBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1),
}));

const AudioPreviewPlayer = ({ audioUrl, duration = 30 }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use a fallback audio URL if none is provided
  const audioSource = audioUrl || 'https://res.cloudinary.com/demo/video/upload/v1689175244/samples/elephants.mp3';
  
  // Debug log
  console.log('AudioPreviewPlayer - Audio URL:', audioUrl);
  console.log('AudioPreviewPlayer - Using source:', audioSource);

  useEffect(() => {
    const audio = audioRef.current;
    
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      // Stop playback after the preview duration
      if (audio.currentTime >= duration) {
        audio.pause();
        audio.currentTime = 0;
        setIsPlaying(false);
      }
    };
    
    const handleLoadedData = () => {
      setIsLoading(false);
      console.log('Audio loaded successfully');
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      audio.currentTime = 0;
    };
    
    const handleError = (e) => {
      console.error('Audio playback error:', e);
      setError('Error loading audio preview');
      setIsLoading(false);
    };
    
    // Add event listeners
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    
    // Set volume
    audio.volume = volume;
    
    // Cleanup
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [duration, volume]);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      // If we've reached the preview limit, start from beginning
      if (currentTime >= duration) {
        audioRef.current.currentTime = 0;
      }
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (_, newValue) => {
    setVolume(newValue);
    audioRef.current.volume = newValue;
    if (newValue === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    if (isMuted) {
      audioRef.current.volume = volume;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  const handleSeek = (_, newValue) => {
    if (newValue <= duration) {
      audioRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <PlayerContainer elevation={0}>
      <PreviewBadge>
        <MusicIcon fontSize="small" sx={{ color: '#4a90e2' }} />
        <Typography variant="subtitle1" fontWeight="500">
          30-Second Preview
        </Typography>
      </PreviewBadge>
      
      {isLoading && <LinearProgress color="primary" />}
      
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      
      <audio ref={audioRef} src={audioSource} preload="metadata" />
      
      <ProgressContainer>
        <Slider
          value={currentTime}
          max={duration}
          onChange={handleSeek}
          aria-label="Audio progress"
          sx={{ 
            color: '#4a90e2',
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0px 0px 0px 8px rgba(74, 144, 226, 0.16)'
              }
            },
            '& .MuiSlider-rail': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        />
        <TimeDisplay>
          {formatTime(currentTime)}/{formatTime(duration)}
        </TimeDisplay>
      </ProgressContainer>
      
      <ControlsContainer>
        <PlayButton onClick={togglePlay}>
          {isPlaying ? <Pause /> : <PlayArrow />}
        </PlayButton>
        
        <IconButton onClick={toggleMute} size="small" sx={{ color: 'white' }}>
          {isMuted ? <VolumeOff /> : <VolumeUp />}
        </IconButton>
        
        <Slider
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          aria-label="Volume"
          min={0}
          max={1}
          step={0.01}
          sx={{ 
            width: 80,
            color: '#4a90e2',
            '& .MuiSlider-thumb': {
              width: 10,
              height: 10
            },
            '& .MuiSlider-rail': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        />
      </ControlsContainer>
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        mt: 2,
        fontSize: '0.75rem',
        color: 'rgba(255, 255, 255, 0.6)',
      }}>
        <InfoIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem' }} />
        <Typography variant="caption">
          Preview limited to 30 seconds. Purchase to listen to the full track.
        </Typography>
      </Box>
    </PlayerContainer>
  );
};

export default AudioPreviewPlayer;
